import React, { createContext, useContext, useState, useMemo, useEffect } from "react";
import { OperationError } from "../DAO";
import { listLicenceOptions } from "./LicenceDAO";


export class Module {
   static parse(source: any = {}): Module {
      return new Module(
         parseRequiredIntProperty('id', source),
         readRequiredProperty('code', source)
      );
   }

   constructor(
      readonly id: number,
      readonly code: string
   ) { }
}

export class ProgramType {
   static parse(source: any = {}): ProgramType {
      return new ProgramType(
         parseRequiredIntProperty('id', source),
         readRequiredProperty('code', source),
         readRequiredProperty('name', source)
      );
   }

   constructor(
      readonly id: number,
      readonly code: string,
      readonly name: string
   ) { }
}

function readRequiredProperty(propName: string, source: any): string {
   if (propName in source) {
      return source[propName];
   }
   throw new Error(`Missing expected property '${propName}'.`);
}

function parseRequiredIntProperty(propName: string, source: any): number {
   const value: number = Number.parseInt(readRequiredProperty(propName, source));
   if (!isNaN(value)) {
      return value;
   }
   throw new Error(`Property '${propName}' is not a invalid integer.`);
}


export type LicenceOptionsStore = {
   readonly isLoading: boolean;
   readonly modules: Module[] | null;
   readonly programTypes: ProgramType[] | null;
   readonly error: OperationError | null;

   readonly loadOptions: () => Promise<void>;
};

const licenceOptionsContext = createContext<LicenceOptionsStore>(null as never);

type Props = {
   children?: React.ReactNode;
};
export const LicenceOptionsProvider = ({ children }: Props): JSX.Element => {
   const [modules, setModules] = useState<Module[] | null>(null);
   const [programTypes, setProgramTypes] = useState<ProgramType[] | null>(null);
   const [error, setError] = useState<OperationError | null>(null);
   const [promise, setPromise] = useState<Promise<void> | null>(null);

   const isLoading: boolean = useMemo(() => promise !== null, [promise]);

   async function loadOptions(): Promise<void> {
      if (promise) { // Already doing a fetch, use that one
         return promise;
      }
      setError(null);
      // Note:  Not clearing existing mods or prog typesuntil loading finishes
      const newPromise = listLicenceOptions()
         .catch((err: OperationError) => {
            setError(err);
            return null;
         })
         .then((results: { modules: Module[], programTypes: ProgramType[] } | null) => {
            setModules(results?.modules ?? null);
            setProgramTypes(results?.programTypes ?? null);
            setPromise(null);
         });
      setPromise(newPromise);
      return newPromise;
   }

   useEffect(() => {
      // initialize
      if ((modules == null || programTypes == null) && error == null && !isLoading) {
         loadOptions();
      }
   }, []);

   return (
      <licenceOptionsContext.Provider value={{ isLoading, modules, programTypes, error, loadOptions }}>
         {children}
      </licenceOptionsContext.Provider>
   );
}

export const useLicenceOptionsStore = (): LicenceOptionsStore => {
   return useContext(licenceOptionsContext);
};
